import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Award, TrendingUp, CheckCircle, Clock, AlertCircle, Trophy } from 'lucide-react';
import api from '../services/api';
import { getUser } from '../utils/auth';

function Dashboard() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = getUser();

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const response = await api.get('/enrollments/my');
        setEnrollments(response.data);
      } catch (error) {
        console.error('Error fetching enrollments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, []);

  const getStatusConfig = (status) => {
    const configs = {
      'screening-pending': {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: Clock,
        label: 'Screening Pending'
      },
      'active': {
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: TrendingUp,
        label: 'In Progress'
      },
      'completed': {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle,
        label: 'Completed'
      },
      'cancelled': {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: AlertCircle,
        label: 'Cancelled'
      }
    };
    return configs[status] || configs['active'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div style={{marginTop: "73px"}} className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-2">
            Welcome back, {user?.name || 'Student'}!
          </h1>
          <p className="text-slate-300 text-lg">
            Track your progress and continue your learning journey
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {enrollments.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-12 text-center">
            <BookOpen className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              No Enrollments Yet
            </h3>
            <p className="text-slate-600 mb-6">
              You haven't enrolled in any programs yet. Explore our programs to get started!
            </p>
            <Link
              to="/programs"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl"
            >
              <BookOpen className="w-5 h-5" />
              Browse Programs
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {enrollments.map((enrollment) => {
              const statusConfig = getStatusConfig(enrollment.enrollmentStatus);
              const StatusIcon = statusConfig.icon;

              return (
                <div
                  key={enrollment._id}
                  className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-lg transition-all overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="p-6 border-b border-slate-100">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-2xl font-bold text-slate-900 flex-1">
                        {enrollment.program?.title}
                      </h3>
                      <span
                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${statusConfig.color}`}
                      >
                        <StatusIcon className="w-4 h-4" />
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    {enrollment.enrollmentStatus === 'active' || enrollment.enrollmentStatus === 'completed' ? (
                      <>
                        {/* Progress Bar */}
                        <div className="mb-6">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-slate-700">
                              Overall Progress
                            </span>
                            <span className="text-sm font-bold text-slate-900">
                              {enrollment.overallProgress}%
                            </span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-slate-900 to-slate-700 h-full rounded-full transition-all duration-500"
                              style={{ width: `${enrollment.overallProgress}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Section Info */}
                        <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 text-slate-700">
                            <BookOpen className="w-5 h-5" />
                            <span className="font-medium">
                              Section {enrollment.currentSection} of {enrollment.sectionsProgress?.length}
                            </span>
                          </div>
                        </div>

                        {/* CTA Buttons */}
                        {enrollment.enrollmentStatus === 'completed' && !enrollment.certificateIssued ? (
                          <div className="space-y-3">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                              <Trophy className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-semibold text-green-800 mb-1">
                                  Congratulations! 🎉
                                </p>
                                <p className="text-green-700 text-sm">
                                  You've completed all sections! Choose your internship path now.
                                </p>
                              </div>
                            </div>
                            <Link
                              to={`/internship/${enrollment._id}`}
                              className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg font-semibold hover:from-green-700 hover:to-green-600 transition-all shadow-lg hover:shadow-xl"
                            >
                              <Award className="w-5 h-5" />
                              Enroll in Internship Program
                            </Link>
                          </div>
                        ) : enrollment.certificateIssued ? (
                          <div className="space-y-3">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                              <CheckCircle className="w-6 h-6 text-green-600" />
                              <span className="font-semibold text-green-800">
                                Certificate Issued ✓
                              </span>
                            </div>
                            <button
                              disabled
                              className="w-full px-6 py-3 bg-slate-200 text-slate-500 rounded-lg font-semibold cursor-not-allowed"
                            >
                              Certificate Already Issued
                            </button>
                          </div>
                        ) : (
                          <Link
                            to={`/learning/${enrollment._id}`}
                            className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl"
                          >
                            <TrendingUp className="w-5 h-5" />
                            Continue Learning
                          </Link>
                        )}
                      </>
                    ) : enrollment.enrollmentStatus === 'screening-pending' ? (
                      <Link
                        to={`/screening/${enrollment._id}`}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600 transition-all shadow-lg hover:shadow-xl"
                      >
                        <Clock className="w-5 h-5" />
                        Complete Screening Assessment
                      </Link>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
